import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Download, MailCheck, Pencil, Plus, Presentation } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, downloadQuote, downloadBlob } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { CLIENT_TYPES, JET_CARD_CATEGORIES, STATUS, TRIP_TYPES, categoryLabel, fileSafe, fmtDate, fmtStamp, isStaff, legsCoveredLabel, money, viewOnlyTrip } from "@/lib/format";
import { ClientTypeBadge } from "@/components/ClientTypeBadge";
import { AsapBadge } from "@/components/AsapBadge";
import StatusBadge from "@/components/StatusBadge";
import OptionCard from "@/components/OptionCard";
import OptionDialog from "@/components/OptionDialog";
import PresentationsPanel from "@/components/PresentationsPanel";
import StatusTimeline from "@/components/StatusTimeline";
import RouteMap from "@/components/RouteMap";
import ClientTypeDialog from "@/components/ClientTypeDialog";
import AcknowledgeDialog from "@/components/AcknowledgeDialog";
import { PageHero } from "@/components/PageHero";
import JetBudgetEditor from "@/components/JetBudgetEditor";
import TripChat from "@/components/TripChat";
import RouteEditDialog from "@/components/RouteEditDialog";
import BallparkEstimator from "@/components/BallparkEstimator";
import TripLifecycleBanner from "@/components/TripLifecycleBanner";
import TripAssignee from "@/components/TripAssignee";
import TripRemoveActions from "@/components/TripRemoveActions";
import TripSwipeNav from "@/components/TripSwipeNav";
import StartTracking from "@/components/StartTracking";
import QuoteFromEmail from "@/components/QuoteFromEmail";
import { PassengerList } from "@/components/PassengerList";
import { CancelTripDialog, NextStep, StatusPipeline } from "@/components/TripPipeline";


function Legs({ legs }) {
  return (
    <div className="lux-card divide-y divide-white/[0.06]" data-testid="trip-legs">
      {legs.map((l, i) => (
        <div key={i} className="grid grid-cols-[50px_1fr_auto] sm:grid-cols-[60px_1fr_1fr_140px_80px] gap-3 px-5 py-4 items-center text-sm">
          <span className="font-mono-data text-[11px] text-[#5B7FD1]">{String(i + 1).padStart(2, "0")}</span>
          <span className="text-white truncate">{l.from_airport}<span className="sm:hidden text-zinc-500"> → {l.to_airport}</span></span>
          <span className="hidden sm:block text-white truncate">→ {l.to_airport}</span>
          <span className="text-zinc-400 text-xs">{fmtDate(l.date)}{l.time ? ` · ${l.time}` : ""}</span>
          <span className="hidden sm:block text-zinc-400 text-xs text-right">{l.pax} pax</span>
        </div>
      ))}
    </div>
  );
}

function Meta({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500">{label}</p>
      <p className="text-sm text-zinc-100 mt-1">{value || "—"}</p>
    </div>
  );
}

function ClientSelection({ s, options, trip, onTripChange }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const opt = options.find((o) => o.id === s.option_id);
  const combo = s.option_ids?.length > 1;
  const marked = combo && s.option_ids.every((id) => (trip.booked_option_ids || []).includes(id));
  const mark = async () => {
    setBusy(true);
    try { onTripChange((await api.post(`/trips/${trip.id}/pick-combo`, { option_ids: s.option_ids })).data); toast.success("Marked as the client's pick"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card !border-[#5B7FD1]/50 bg-[#5B7FD1]/[0.04] p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="client-selection-banner">
      <CheckCircle2 className="text-[#5B7FD1] shrink-0" size={22} strokeWidth={1.4} />
      <div className="flex-1 text-sm">
        <p className="text-white">{s.name} selected {s.aircraft_type} at {money(s.client_price, s.currency)}. Ready to book.</p>
        <p className="text-xs text-zinc-400 mt-1">
          {combo ? s.parts.map((pt) => `${legsCoveredLabel(pt.legs_covered, trip.legs) || "All legs"}: ${pt.aircraft_type} ${money(pt.client_price, s.currency)}`).join(" · ")
            : opt ? `Operator option ${opt.rank}${opt.operator_name ? `: ${opt.operator_name}` : ""}${opt.tail_number ? ` · ${opt.tail_number}` : ""}` : "The underlying option has since been removed"}
          {s.contact ? ` · Contact: ${s.contact}` : ""} · {fmtDate(s.at)} · via “{s.presentation_title}”
        </p>
        {s.note && <p className="text-xs text-zinc-300 mt-2">“{s.note}”</p>}
      </div>
      {onTripChange && combo && ["new", "options_sent", "booking_requested"].includes(trip.status) && !marked && (
        <button className="frame-btn gold shrink-0" disabled={busy} onClick={mark} data-testid="client-selection-mark-combo-btn">Client wants these</button>
      )}
    </div>
  );
}

export default function TripDetail() {
  const { id } = useParams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const swipeDir = useMemo(() => Number(sessionStorage.getItem("jetset-swipe-dir") || 0), [id]);
  useEffect(() => { sessionStorage.removeItem("jetset-swipe-dir"); }, [id]);
  const [search] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const staff = isStaff(me);
  const exec = me.role === "executive";
  const [trip, setTrip] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dialog, setDialog] = useState({ open: false, option: null });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [picked, setPicked] = useState([]);
  const [launch, setLaunch] = useState(null);
  const [clientEdit, setClientEdit] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [qCounts, setQCounts] = useState({});

  const togglePick = (o) => setPicked((p) => (p.includes(o.id) ? p.filter((x) => x !== o.id) : [...p, o.id]));

  const customerPdf = async (opts) => {
    setPdfBusy(true);
    try {
      const qs = opts ? `?option_ids=${opts.map((o) => o.id).join(",")}` : "";
      await downloadBlob(api, `/trips/${trip.id}/customer.pdf${qs}`, `JetSet-Customer-Quote-${fileSafe(trip.ref)}.pdf`);
    } catch (e) {
      const detail = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : null;
      toast.error(detail || "Could not generate the customer PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const pdfSelected = async () => {
    const chosen = trip.options.filter((o) => picked.includes(o.id));
    if (!chosen.length) return;
    setPdfBusy(true);
    try {
      await downloadBlob(api, `/trips/${trip.id}/quote.pdf?option_ids=${chosen.map((o) => o.id).join(",")}`,
        `JetSet-Quote-${fileSafe(trip.ref)}-options-${chosen.map((o) => o.rank).join("-")}.pdf`);
    } catch (e) {
      toast.error("Could not generate the PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/trips/${id}`);
      setTrip(data);
      api.get(`/trips/${id}/messages/counts`).then((r) => setQCounts(r.data)).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(errMsg(e));
    }
  }, [api, id]);

  useEffect(() => {
    setTrip(null);
    setPicked([]);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (status) => {
    if (status === "cancelled") { setCancelOpen(true); return; }
    try {
      await api.patch(`/trips/${id}`, { status, notify: true });
      if (status === "completed") {
        toast.success("Trip completed and moved to Trash. Broker notified.");
        navigate("/");
        return;
      }
      toast.success(`Status set to ${STATUS[status].label}. Broker notified.`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pdf = async (option) => {
    setPdfBusy(true);
    try {
      await downloadQuote(api, trip, option);
    } catch (e) {
      toast.error("Could not generate the PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const del = async (o) => {
    try {
      await api.delete(`/options/${o.id}`);
      toast.success("Option removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const refresh = async (o) => {
    toast.promise(api.post(`/options/${o.id}/refresh-photos`).then(load), { loading: "Searching photos…", success: "Photos refreshed", error: "Photo search failed" });
  };

  if (notFound) return (
    <div className="py-24 text-center" data-testid="trip-not-found">
      <p className="font-serif-display text-4xl text-white">Trip not found</p>
      <Link to="/" className="frame-btn mt-8">Back to trips</Link>
    </div>
  );
  if (!trip) return <div className="py-24 grid place-items-center"><div className="jet-loader" /></div>;
  const needAck = !trip.acknowledged_at && trip.status === "new";

  return (
    <div key={id} className={swipeDir ? (swipeDir > 0 ? "trip-slide-next" : "trip-slide-prev") : ""} data-testid="trip-detail-page">
      <PageHero testId="trip-hero">
      <div className="flex items-center justify-between gap-3 mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.22em] uppercase text-zinc-300 hover:text-white" data-testid="back-to-trips-link">
          <ArrowLeft size={13} /> All trips
        </Link>
        <TripSwipeNav id={id} />
      </div>
      <div className="flex flex-col gap-6">
        <div className="rise">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <StatusBadge status={trip.status} testId="trip-status-badge" />
            <AsapBadge trip={trip} testId="trip-asap-badge" />
            <ClientTypeBadge trip={trip} testId="trip-client-badge" showOnDemand={!!trip.client_type} />
          </div>
          <h1 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-white leading-[1.2] break-words" data-testid="trip-ref">{trip.ref}</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          {staff && needAck && (
            <button className="frame-btn gold ack-pulse" onClick={() => setAckOpen(true)} data-testid="acknowledge-request-btn">
              <MailCheck size={13} /> Acknowledge request
            </button>
          )}
          {staff && (
            <Select value={trip.status} onValueChange={changeStatus} disabled={needAck}>
              <SelectTrigger className="w-[260px] h-[46px] text-xs tracking-[0.12em] uppercase" title={needAck ? "Acknowledge the request first" : undefined} data-testid="trip-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {trip.client_type === "jet_card" && (
            <button className="frame-btn" onClick={() => customerPdf(null)} disabled={pdfBusy || !trip.options.some((o) => o.customer_price)} data-testid="download-customer-pdf-btn">
              <Download size={13} /> Customer PDF
            </button>
          )}
          <button className="frame-btn outline-gold" onClick={() => pdf(null)} disabled={pdfBusy} data-testid="download-quote-pdf-btn">
            <Download size={13} /> {pdfBusy ? "Generating…" : "Quote PDF"}
          </button>
          {staff && (
            <button className="frame-btn gold" disabled={needAck} title={needAck ? "Acknowledge the request first" : undefined} onClick={() => setDialog({ open: true, option: null })} data-testid="add-option-btn">
              <Plus size={13} /> Add option
            </button>
          )}
          {staff && ["new", "options_sent", "booking_requested"].includes(trip.status) && <QuoteFromEmail trip={trip} disabled={needAck} onAdded={load} />}
          <StartTracking trip={trip} staff={staff} />
          {!viewOnlyTrip(me, trip) && <NextStep trip={trip} staff={staff} onChanged={load} />}
        </div>
        <StatusPipeline trip={trip} />
        {viewOnlyTrip(me, trip) && (
          <p className="basis-full text-xs text-[#E2C989]" data-testid="view-only-note">View only: this is {trip.broker_name}'s trip. You can read everything but can't make changes.</p>
        )}
        {staff && needAck && (
          <p className="basis-full text-xs text-[#E2C989]" data-testid="ack-required-note">Acknowledge this request to start working on it. Status changes and options unlock once it's acknowledged.</p>
        )}
      </div>
      </PageHero>

      {staff && <ClientTypeDialog trip={trip} open={clientEdit} onOpenChange={setClientEdit} onSaved={load} />}
      {staff && <AcknowledgeDialog trip={trip} open={ackOpen} onOpenChange={setAckOpen} onSaved={load} />}
      {staff && <CancelTripDialog trip={trip} open={cancelOpen} onOpenChange={setCancelOpen} onSaved={load} />}
      {trip.acknowledged_at && (
        <div className="flex items-start gap-2.5 text-xs text-emerald-300/90 mb-6" data-testid="trip-acknowledged-info">
          <MailCheck size={14} className="mt-0.5 shrink-0" />
          <span>
            Acknowledged by {trip.acknowledged_by_name || "the flight team"} · {fmtStamp(trip.acknowledged_at)}
            {trip.acknowledged_note && <span className="block text-zinc-400 mt-0.5">Note: {trip.acknowledged_note}</span>}
          </span>
        </div>
      )}
      <TripLifecycleBanner trip={trip} isAdmin={me.role === "admin"} isBroker={!staff && !viewOnlyTrip(me, trip)} onChanged={setTrip} />
      <RouteEditDialog trip={trip} open={routeOpen} onOpenChange={setRouteOpen} onSaved={setTrip} />
      {trip.client_selection && <ClientSelection s={trip.client_selection} options={trip.options} trip={trip} onTripChange={viewOnlyTrip(me, trip) ? null : setTrip} />}
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 mb-14">
        <div className="space-y-6 self-start min-w-0">
          <RouteMap legs={trip.legs} className="" testId="trip-route-map" />
          <div className="flex flex-wrap items-center justify-between gap-3 -mb-3">
            <BallparkEstimator legs={trip.legs} category={trip.aircraft_category} testId="trip-ballpark" />
            {!trip.archived_at && !['completed', 'cancelled'].includes(trip.status) && !viewOnlyTrip(me, trip) && (
              <button className="inline-flex items-center gap-2 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-400 hover:text-[#5B7FD1] transition-colors" onClick={() => setRouteOpen(true)} data-testid="edit-route-btn">
                <Pencil size={12} /> Edit route
              </button>
            )}
          </div>
          <Legs legs={trip.legs} />
          <PassengerList tripId={trip.id} legs={trip.legs} staff={staff} onSaved={load} />
          <StatusTimeline trip={trip} />
        </div>
        <div className="space-y-6 self-start min-w-0">
        <div className="lux-card p-5 grid grid-cols-2 gap-5 content-start">
          <Meta label="Broker" value={trip.broker_name} testId="trip-broker" />
          <Meta label="Trip type" value={TRIP_TYPES.find((t) => t.value === trip.trip_type)?.label} />
          {staff && <TripAssignee trip={trip} isAdmin={me.role === "admin"} onSaved={setTrip} autoOpen={search.get("reassign") === "1"} />}
          {exec && <Meta label="Assigned to" value={trip.assigned_to_name || "Not assigned"} testId="trip-assignee-readonly" />}
          <div className="flex items-start justify-between gap-2">
            <Meta label="Client type" value={CLIENT_TYPES.find((c) => c.value === trip.client_type)?.label} testId="trip-client-type" />
            {staff && (
              <button type="button" onClick={() => setClientEdit(true)} className="text-zinc-500 hover:text-[#5B7FD1] transition-colors mt-0.5" title="Edit client type" data-testid="edit-client-type-btn">
                <Pencil size={13} />
              </button>
            )}
          </div>
          <Meta label={trip.client_type === "jet_card" ? "Jet Card category" : "Category"}
            value={trip.client_type === "jet_card" ? JET_CARD_CATEGORIES.find((c) => c.value === trip.aircraft_category)?.label : categoryLabel(trip.aircraft_category)} />
          {trip.client_type === "jet_card" && !viewOnlyTrip(me, trip) && <JetBudgetEditor trip={trip} onSaved={setTrip} />}
          {trip.client_reference && <Meta label="Client ref" value={trip.client_reference} />}
          <div className="col-span-2" data-testid="trip-add-ons-meta">
            <p className="text-[0.58rem] tracking-[0.22em] uppercase text-zinc-500">Add-ons</p>
            {trip.ground_transportation || trip.catering ? (
              [["ground_transportation", "Ground transportation"], ["catering", "Catering"]].filter(([k]) => trip[k]).map(([k, label]) => (
                <p key={k} className="text-sm text-zinc-100 mt-1" data-testid={`trip-add-on-${k}`}>
                  {label}{trip[`${k}_details`] && <span className="text-zinc-400 whitespace-pre-line">: {trip[`${k}_details`]}</span>}
                </p>
              ))
            ) : <p className="text-sm text-zinc-100 mt-1">None</p>}
          </div>
          <Meta label="Requested" value={fmtDate(trip.created_at)} />
          <Meta label="Source" value={trip.source === "broker_portal" ? "JSG Portal" : "Flight team"} />
          {trip.notes && <div className="col-span-2"><Meta label="Notes" value={trip.notes} /></div>}
        </div>
        <TripChat trip={trip} onChanged={load} />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
        <div>
          <p className="eyebrow mb-2">Certified charter options</p>
          <h2 className="font-serif-display text-3xl text-white">Ranked by price</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-zinc-500" data-testid="option-count">{trip.options.length} option{trip.options.length === 1 ? "" : "s"}</span>
          {trip.options.length > 1 && (
            <button className="text-[0.62rem] tracking-[0.2em] uppercase text-zinc-400 hover:text-[#5B7FD1] transition-colors" data-testid="select-all-toggle-btn"
              onClick={() => setPicked(picked.length === trip.options.length ? [] : trip.options.map((o) => o.id))}>
              {picked.length === trip.options.length ? "Clear" : "Select all"}
            </button>
          )}
          {trip.options.length > 1 && (
            <button className="frame-btn !py-2.5 !px-4" onClick={pdfSelected} disabled={!picked.length || pdfBusy} data-testid="download-selected-pdf-btn">
              <Download size={13} /> {picked.length ? `PDF of ${picked.length} selected` : "Pick options for one PDF"}
            </button>
          )}
          {picked.length > 0 && trip.client_type === "jet_card" && (
            <button className="frame-btn !py-2.5 !px-4" disabled={pdfBusy} onClick={() => customerPdf(trip.options.filter((o) => picked.includes(o.id)))} data-testid="customer-pdf-selected-btn">
              <Download size={13} /> Customer PDF of {picked.length}
            </button>
          )}
          {picked.length > 0 && trip.client_type !== "jet_card" && !viewOnlyTrip(me, trip) && (
            <button className="frame-btn gold !py-2.5 !px-4" onClick={() => setLaunch({ ids: [...picked], at: Date.now() })} data-testid="present-selected-btn">
              <Presentation size={13} /> Present {picked.length} to client
            </button>
          )}
        </div>
      </div>
      {trip.options.length > 0 && (trip.options.length > 1 || staff) && (
        <p className="md:hidden text-[11px] text-zinc-500 mb-3" data-testid="options-swipe-hint">
          {trip.options.length > 1 ? "Swipe right on an option to select it" : ""}{trip.options.length > 1 && staff ? " · " : ""}{staff ? "Swipe left to set its status" : ""}
        </p>
      )}
      <div className="space-y-4" data-testid="options-list">
        {trip.options.length === 0 && (
          <div className="lux-card px-6 py-14 text-center" data-testid="options-empty">
            <p className="font-serif-display text-2xl text-white">Options coming soon</p>
            <p className="text-zinc-500 text-sm mt-2">{staff ? "Add the first operator option for this broker." : "Our flight department is gathering quotes. You'll be emailed as options arrive."}</p>
          </div>
        )}
        {trip.options.map((o, idx) => (
          <Fragment key={o.id}>
          {trip.options.some((x) => x.legs_covered?.length) && (idx === 0 || (o.legs_covered || []).join(",") !== (trip.options[idx - 1].legs_covered || []).join(",")) && (
            <p className="eyebrow pt-2" data-testid={`options-group-${(o.legs_covered || []).join("-") || "all"}`}>{legsCoveredLabel(o.legs_covered, trip.legs) || "All legs"}</p>
          )}
          <OptionCard o={o} staff={staff} showInternal={staff || exec} readOnly={viewOnlyTrip(me, trip)} jetCard={trip.client_type === "jet_card"} picked={picked.includes(o.id)} onPick={trip.options.length > 1 ? togglePick : null} selected={(trip.client_selection?.option_ids || [trip.client_selection?.option_id]).includes(o.id)} onPdf={pdf} onCustomerPdf={trip.client_type === "jet_card" ? customerPdf : null} onDelete={del} onRefresh={refresh} onPhotosChanged={load}
            onEdit={(opt) => setDialog({ open: true, option: opt })} onTripChange={viewOnlyTrip(me, trip) ? null : (data) => setTrip(data)}
            trip={trip} qCount={qCounts[o.id] || 0} onAsked={load} />
          </Fragment>
        ))}
      </div>
      {trip.client_type === "jet_card" ? (
        <p className="lux-card px-6 py-5 mt-10 text-sm text-zinc-400" data-testid="presentations-jet-card-note">
          Client presentations are available for On Demand Charter trips only.
        </p>
      ) : viewOnlyTrip(me, trip) ? null : (
        <PresentationsPanel trip={trip} launch={launch} />
      )}
      {staff && <TripRemoveActions trip={trip} isAdmin={me.role === "admin"} />}
      {staff && (
        <OptionDialog open={dialog.open} option={dialog.option} tripId={trip.id} trip={trip}
          onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={load} />
      )}
    </div>
  );
}
